import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFileExcel, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { SelectWithSearch, type SelectOption } from '../../utils/select-with-search';
import { dateRangeError, dateRangeInputBounds } from '../../utils/date-range.utils';

/**
 * Site & FW Start Difference
 * =========================
 *
 * How far the engineer was from the site when they started its field work: the
 * site's registered coordinates against the position stamped at the moment
 * field work began, as an aerial (straight-line) distance.
 *
 * The "greater than 2 km" filter and the sort are applied by the API, not here
 * — filtering a page client-side would return short pages and a wrong total.
 */

interface ReportRow {
  id: number;
  site_id: string | null;
  site_name: string | null;
  user_name: string | null;
  employee_code: string | null;
  project_name: string | null;
  site_lat_long: string | null;
  field_work_start_at: string | null;
  field_work_start_lat_long: string | null;
  aerial_distance_km: number | null;
  beyond_threshold: boolean | null;
  [key: string]: any;
}

interface Filters {
  user_id: string;
  project_id: string;
  department_id: string;
  start_date_from: string;
  start_date_to: string;
  /** 'all' | 'greater_than_2km' */
  difference: string;
  search: string;
  sort_order: 'ASC' | 'DESC';
}

const DEFAULT_FILTERS: Filters = {
  user_id: '',
  project_id: '',
  department_id: '',
  start_date_from: '',
  start_date_to: '',
  difference: 'all',
  search: '',
  sort_order: 'DESC',
};

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const EMPTY_PAGINATION: NormalizedPagination = {
  total_items: 0,
  current_page: 1,
  total_pages: 1,
  items_per_page: 50,
  has_next_page: false,
  has_previous_page: false,
};

const TABLE_COLUMNS: { key: keyof ReportRow; label: string }[] = [
  { key: 'site_id', label: 'Site ID' },
  { key: 'user_name', label: 'User Name' },
  { key: 'project_name', label: 'Project Name' },
  { key: 'site_lat_long', label: 'Site LatLong' },
  { key: 'field_work_start_at', label: 'Field Work Start Date & Time' },
  { key: 'field_work_start_lat_long', label: 'Field Work Start LatLong' },
  { key: 'aerial_distance_km', label: 'Aerial Distance (km)' },
];

const formatDateTime = (value: unknown): string => {
  if (!value) return 'NA';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const display = (row: ReportRow, key: keyof ReportRow): string => {
  const value = row[key];
  if (key === 'field_work_start_at') return formatDateTime(value);
  if (key === 'aerial_distance_km') {
    return value === null || value === undefined ? 'NA' : `${Number(value).toFixed(2)} km`;
  }
  if (value === null || value === undefined || value === '') return 'NA';
  return String(value);
};

const SiteFwStartReport: React.FC = () => {
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null);
  const [selectedUser, setSelectedUser] = useState<SelectOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption | null>(null);

  const [draftFilters, setDraftFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(DEFAULT_FILTERS);

  const [rows, setRows] = useState<ReportRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>(EMPTY_PAGINATION);
  const [thresholdKm, setThresholdKm] = useState(2);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Dropdowns load independently: one failing endpoint must not leave the whole
  // filter form empty with nothing said about which call failed.
  const loadFrom = useCallback(
    async <T,>(
      path: string,
      label: string,
      pick: (data: any) => T,
      apply: (value: T) => void,
      params?: Record<string, string | number>,
    ) => {
      try {
        const response = await axiosInstance.get(path, { params });
        if (!response.data?.success) throw new Error(response.data?.message || label);
        apply(pick(response.data.data || {}));
      } catch (error: any) {
        console.error(`[site-fw-start] ${path} failed:`, error);
        toast.error(
          error?.response?.data?.message || `Failed to load ${label.toLowerCase()}`,
          { id: `sfs-filter-${path}` },
        );
      }
    },
    [],
  );

  useEffect(() => {
    loadFrom(
      '/reports/site-fw-start/projects',
      'Projects',
      (data) =>
        (data.projects || []).map((project: any) => ({
          id: project.id,
          value: project.id,
          label: project.project_short_name
            ? `${project.project_name} (${project.project_short_name})`
            : project.project_name,
        })) as SelectOption[],
      setProjectOptions,
    );
    loadFrom(
      '/reports/site-fw-start/departments',
      'Departments',
      (data) =>
        (data.departments || []).map((department: any) => ({
          id: department.id,
          value: department.id,
          label: department.name,
        })) as SelectOption[],
      setDepartmentOptions,
    );
  }, [loadFrom]);

  // The engineer list follows the picked project, so it never offers someone
  // who cannot appear in the result.
  useEffect(() => {
    loadFrom(
      '/reports/site-fw-start/users',
      'Users',
      (data) =>
        (data.users || []).map((user: any) => ({
          id: user.id,
          value: user.id,
          label: user.employee_code ? `${user.name} (${user.employee_code})` : user.name,
        })) as SelectOption[],
      setUserOptions,
      draftFilters.project_id ? { project_id: draftFilters.project_id } : undefined,
    );
  }, [draftFilters.project_id, loadFrom]);

  const buildParams = (page: number, limit: number, filters: Filters) => {
    const params: Record<string, string | number> = { page, limit };
    if (filters.user_id) params.user_id = Number(filters.user_id);
    if (filters.project_id) params.project_id = Number(filters.project_id);
    if (filters.department_id) params.department_id = Number(filters.department_id);
    if (filters.start_date_from) params.start_date_from = filters.start_date_from;
    if (filters.start_date_to) params.start_date_to = filters.start_date_to;
    if (filters.difference) params.difference = filters.difference;
    if (filters.search) params.search = filters.search;
    if (filters.sort_order) params.sort_order = filters.sort_order;
    return params;
  };

  const fetchRows = async (
    targetPage = 1,
    targetLimit = pagination.items_per_page,
    filters: Filters = appliedFilters,
  ) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/site-fw-start', {
        params: buildParams(targetPage, targetLimit, filters),
      });
      if (!response.data?.success) return;

      const payload = response.data.data || {};
      const data: ReportRow[] = payload.data || [];
      const meta = payload.pagination || {};
      const total = Number(meta.total ?? data.length) || 0;
      const currentPage = Number(meta.current_page ?? targetPage) || targetPage;
      const perPage = Number(meta.per_page ?? targetLimit) || targetLimit;
      const totalPages = Number(meta.total_pages) || Math.max(1, Math.ceil(total / perPage));

      setRows(data);
      setThresholdKm(Number(payload.threshold_km ?? 2));
      setPagination({
        total_items: total,
        current_page: currentPage,
        total_pages: totalPages,
        items_per_page: perPage,
        has_next_page: Boolean(meta.has_next_page ?? currentPage < totalPages),
        has_previous_page: Boolean(meta.has_prev_page ?? currentPage > 1),
      });
      setHasFetched(true);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to fetch the report');
    } finally {
      setLoading(false);
    }
  };

  // Built server-side so the workbook can never drift from the table.
  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await axiosInstance.get('/reports/site-fw-start/export', {
        params: buildParams(1, pagination.items_per_page, appliedFilters),
        responseType: 'blob',
      });

      const disposition = String(response.headers?.['content-disposition'] || '');
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fileName = match ? decodeURIComponent(match[1]) : 'site-fw-start-difference.xlsx';

      const blobUrl = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Export downloaded');
    } catch (error: any) {
      let message = 'Failed to export the report';
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text())?.message || message;
        } catch {
          /* not JSON — keep the generic message */
        }
      } else if (data?.message) {
        message = data.message;
      }
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const paginationAdapter: PaginationType = useMemo(
    () => ({
      offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
      limit: pagination.items_per_page,
      page: pagination.current_page,
      total_pages: pagination.total_pages,
      total_count: pagination.total_items,
      has_next_page: pagination.has_next_page,
      has_prev_page: pagination.has_previous_page,
      next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
      prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null,
    }),
    [pagination],
  );

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    const dateError = dateRangeError(draftFilters.start_date_from, draftFilters.start_date_to, 'Start Date');
    if (dateError) {
      toast.error(dateError);
      return;
    }
    setAppliedFilters(draftFilters);
    fetchRows(1, pagination.items_per_page, draftFilters);
  };

  const handleReset = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setSelectedProject(null);
    setSelectedUser(null);
    setSelectedDepartment(null);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Site &amp; FW Start Difference</h1>
            <p className="text-sm text-gray-600 mt-1">
              Aerial distance between a site and the position field work on it was started from.
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={!hasFetched || exporting}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
          >
            {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
            {exporting ? 'Preparing...' : 'Export Excel'}
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          <p className="text-sm text-gray-600">
            Every filter is optional. Only sites whose field work has actually started appear —
            a site that was never opened has no start position to measure against.
          </p>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">User Name</span>
            <SelectWithSearch
              placeholder="All users"
              value={selectedUser}
              options={userOptions}
              onChange={(option) => {
                setSelectedUser(option);
                handleFilterChange('user_id', option ? String(option.value) : '');
              }}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Project Name</span>
            <SelectWithSearch
              placeholder="All projects"
              value={selectedProject}
              options={projectOptions}
              onChange={(option) => {
                setSelectedProject(option);
                // The user list is scoped to the picked project, so a user
                // chosen under the old selection may no longer be in it.
                setSelectedUser(null);
                setDraftFilters((prev) => ({
                  ...prev,
                  project_id: option ? String(option.value) : '',
                  user_id: '',
                }));
              }}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Department Name</span>
            <SelectWithSearch
              placeholder="All departments"
              value={selectedDepartment}
              options={departmentOptions}
              onChange={(option) => {
                setSelectedDepartment(option);
                handleFilterChange('department_id', option ? String(option.value) : '');
              }}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Difference</span>
            <select
              value={draftFilters.difference}
              onChange={(event) => handleFilterChange('difference', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value="all">All</option>
              <option value="greater_than_2km">Greater than 2 Km</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Start Date From</span>
            <input
              type="date"
              value={draftFilters.start_date_from}
              onChange={(event) => handleFilterChange('start_date_from', event.target.value)}
              {...dateRangeInputBounds(draftFilters.start_date_from, draftFilters.start_date_to).from}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Start Date To</span>
            <input
              type="date"
              value={draftFilters.start_date_to}
              onChange={(event) => handleFilterChange('start_date_to', event.target.value)}
              {...dateRangeInputBounds(draftFilters.start_date_from, draftFilters.start_date_to).to}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Search</span>
            <input
              type="text"
              value={draftFilters.search}
              onChange={(event) => handleFilterChange('search', event.target.value)}
              placeholder="Site ID or name"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Distance Sort</span>
            <select
              value={draftFilters.sort_order}
              onChange={(event) => handleFilterChange('sort_order', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value="DESC">Largest difference first</option>
              <option value="ASC">Smallest difference first</option>
            </select>
          </label>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={handleReset}
            className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed"
          >
            {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : null}
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {hasFetched && (
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-gray-700">
              {appliedFilters.difference === 'greater_than_2km'
                ? `Showing only sites more than ${thresholdKm} km from where field work started`
                : 'Showing every site whose field work has started'}
            </p>
            <p className="text-sm text-gray-500">{pagination.total_items} site(s)</p>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {TABLE_COLUMNS.map((column) => (
                  <th
                    key={String(column.key)}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                    Loading...
                  </td>
                </tr>
              ) : !hasFetched ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                    Apply filters to load the report.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                    No sites found for these filters.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {TABLE_COLUMNS.map((column) => (
                      <td
                        key={String(column.key)}
                        className={`px-4 py-3 text-sm align-top whitespace-nowrap ${
                          column.key === 'aerial_distance_km' && row.beyond_threshold
                            ? 'font-semibold text-red-700'
                            : 'text-gray-700'
                        }`}
                      >
                        {display(row, column.key)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {hasFetched && (
          <Pagination
            pagination={paginationAdapter}
            onPageChange={(nextPage) => fetchRows(nextPage, pagination.items_per_page, appliedFilters)}
            onLimitChange={(nextLimit) => fetchRows(1, nextLimit, appliedFilters)}
          />
        )}
      </div>
    </div>
  );
};

export default SiteFwStartReport;
